import type { TimezoneArea } from "@/types/tradingTool";

export const TIMEZONE_AREAS: TimezoneArea[] = [
  { id: "cest", area: "CEST Time", timeZone: "Europe/Berlin" },
  { id: "platform", area: "URFX Platform Time", timeZone: "Europe/Berlin" },
  { id: "local", area: "Your Local Time", timeZone: "local" },
  { id: "new-york", area: "New York Time", timeZone: "America/New_York" },
  { id: "london", area: "London Time", timeZone: "Europe/London" },
  { id: "tokyo", area: "Tokyo Time", timeZone: "Asia/Tokyo" },
];

export const PLATFORM_TIMEZONE = "Europe/Berlin";
export const NEW_YORK_TIMEZONE = "America/New_York";

export function formatNewYorkDateTime(value: string | number | Date | null | undefined): string {
  if (value == null || value === "") return "-";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    timeZone: NEW_YORK_TIMEZONE, timeZoneName: "short",
  });
}

export function formatNewYorkDate(value: string | number | Date | null | undefined): string {
  if (value == null || value === "") return "-";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-US", {
    month: "short", day: "numeric", timeZone: NEW_YORK_TIMEZONE, timeZoneName: "short",
  });
}

const DAILY_LOSS_START_H = 0;
const DAILY_LOSS_START_M = 0;
const DAILY_LOSS_END_H = 23;
const DAILY_LOSS_END_M = 59;

function resolveTimeZone(timeZone: string): string {
  if (timeZone !== "local") return timeZone;
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function getUtcOffsetMinutes(timeZone: string, date: Date): number {
  const tz = resolveTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeZoneName: "shortOffset",
  }).formatToParts(date);

  const offsetPart = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT";
  const match = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return 0;

  const sign = match[1] === "+" ? 1 : -1;
  const hours = Number(match[2]);
  const minutes = Number(match[3] ?? 0);
  return sign * (hours * 60 + minutes);
}

export function formatClockTime(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: resolveTimeZone(timeZone),
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

export function formatCestOffset(timeZone: string, date: Date): string {
  const tz = resolveTimeZone(timeZone);
  const platformTz = PLATFORM_TIMEZONE;

  if (tz === platformTz) return "-";

  const diffMinutes =
    getUtcOffsetMinutes(tz, date) - getUtcOffsetMinutes(platformTz, date);
  const diffHours = Math.round(diffMinutes / 60);

  if (diffHours === 0) return "-";

  const abs = Math.abs(diffHours);
  const unit = abs === 1 ? "hr" : "hrs";
  return diffHours > 0 ? `+${abs} ${unit}` : `${abs} ${unit}`;
}

function getPlatformDateKey(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PLATFORM_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function findPlatformMoment(
  dateKey: string,
  hour: number,
  minute: number,
  around: Date,
): Date | null {
  const targetTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

  for (
    let timestamp = around.getTime() - 36e5 * 30;
    timestamp < around.getTime() + 36e5 * 30;
    timestamp += 60_000
  ) {
    const probe = new Date(timestamp);
    const probeDate = getPlatformDateKey(probe);
    const probeTime = new Intl.DateTimeFormat("en-GB", {
      timeZone: PLATFORM_TIMEZONE,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(probe);

    if (probeDate === dateKey && probeTime === targetTime) {
      return probe;
    }
  }

  return null;
}

function formatDailyBoundary(hour: number, minute: number, targetZone: string): string {
  const now = new Date();
  const dateKey = getPlatformDateKey(now);
  const moment = findPlatformMoment(dateKey, hour, minute, now);

  if (!moment) return "--:--";
  return formatClockTime(moment, targetZone).slice(0, 5);
}

export function getDailyLossStart(timeZone: string): string {
  return formatDailyBoundary(DAILY_LOSS_START_H, DAILY_LOSS_START_M, timeZone);
}

export function getDailyLossEnd(timeZone: string): string {
  return formatDailyBoundary(DAILY_LOSS_END_H, DAILY_LOSS_END_M, timeZone);
}

export function getDailyLossResetCountdown(now: Date): string {
  const dateKey = getPlatformDateKey(now);
  const midnight = findPlatformMoment(dateKey, 0, 0, now);
  if (!midnight) return "00:00:00";

  let nextReset = midnight.getTime();
  if (nextReset <= now.getTime()) {
    nextReset += 24 * 60 * 60 * 1000;
  }

  const diffMs = nextReset - now.getTime();
  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((v) => String(v).padStart(2, "0"))
    .join(":");
}
